import mpesaLogo from "@/assets/mpesa.png";
import equityLogo from "@/assets/equity.jpeg";
import kcbLogo from "@/assets/kcb.png";
import telkomLogo from "@/assets/telkom.jpeg";
import airtelLogo from "@/assets/airtel.jpeg";
import standardCharteredLogo from "@/assets/standardchartered.jpeg";
import familyBankLogo from "@/assets/family.png";
import { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { selectUsers } from '@/State/usersSlice';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  DollarSign, 
  CreditCard, 
  Receipt, 
  Users, 
  TrendingUp, 
  FileText,
  PlusCircle,
  Download,
  AlertCircle,
  Loader2,
  Settings
} from "lucide-react";
import Header from "@/components/Header";
import AdminSidebar from "@/components/Sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { db, auth, functions } from "@/firebase/config";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { collection, getDoc, getDocs, doc, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import TeacherSidebar from "@/Teacherdashboard/Teachersidebar";
import Teacherheader from "@/Teacherdashboard/Teacherheader";
import { getApplicableTermFees } from "@/utils/feeCalculations";

// Import finance components
import StudentFeeManagement from "@/components/finance/StudentFeeManagement";
import PaymentProcessing from "@/components/finance/PaymentProcessing";
import ExpenseManagement from "@/components/finance/ExpenseManagement";
import PayrollManagement from "@/components/finance/PayrollManagement";
import FinanceReports from "@/components/finance/FinanceReports";
import FinanceDashboard from "@/components/finance/FinanceDashboard";
import CreateFeeStructure from "@/components/CreateFeeStructure";
import FinanceSettings from "@/components/finance/FinanceSettings";
import NewTransaction from "@/components/finance/NewTransaction";

const FinanceManagement = () => {
  const { currentUser } = useSelector(selectUsers);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [schoolID, setSchoolID] = useState<string | null>(currentUser?.selectedAffiliation?.schoolID || null);
  const [userRole, setUserRole] = useState<string | null>(currentUser?.selectedAffiliation?.role || null);
  const [providerList, setProviderList] = useState<string[]>([]);
  const [showFeeStructure, setShowFeeStructure] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNewTransaction, setShowNewTransaction] = useState(false);
  type ProviderBalance = { value: number | null; loading: boolean; source: 'api'|'firestore'|'mock'; error?: string };
  const [providerBalances, setProviderBalances] = useState<Record<string, ProviderBalance>>({});
  const [outstandingFees, setOutstandingFees] = useState<number | null>(null);
  const [isLoadingOutstanding, setIsLoadingOutstanding] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const addDebugLog = (message: string, data?: any) => {
    const ts = new Date().toISOString();
    setDebugLogs((prev) => [
      ...prev,
      data !== undefined ? `[${ts}] ${message}\n${JSON.stringify(data, null, 2)}` : `[${ts}] ${message}`,
    ]);
  };

  // capture runtime errors globally for quick copy
  useEffect(() => {
    const onErr = (event: ErrorEvent) => {
      addDebugLog("window.error", { message: event.message, filename: event.filename, lineno: event.lineno, colno: event.colno });
    };
    const onRej = (event: PromiseRejectionEvent) => {
      addDebugLog("unhandledrejection", { reason: String(event.reason) });
    };
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej as any);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej as any);
    };
  }, []);

  // Helpers (no longer need term ranges for annual calculation)
  const fmtKES = (n: number | null) => n == null ? "—" : `KES ${Math.round(n).toLocaleString()}`;

  // Get admin/school_staff schoolID from auth claims
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { 
        setSchoolID(null); 
        setUserRole(null); 
        return; 
      }
      try {
        const token = await getIdTokenResult(u, true);
        const affiliations = (token.claims.affiliations as Array<{ role: string; schoolID?: string }> | undefined) || [];
        const defaultAff = token.claims.defaultAffiliation as { role?: string; schoolID?: string } | undefined;
        const pick =
          (affiliations.find(a => a.role === 'admin' && a.schoolID?.trim()) ||
           affiliations.find(a => a.role === 'school_staff' && a.schoolID?.trim()) ||
           defaultAff);
        const sid = pick?.schoolID?.trim() || null;
        const role = pick?.role || null;
        setSchoolID(sid);
        setUserRole(role);
        addDebugLog('FinanceManagement: Derived auth', { schoolID: sid, role });
      } catch (e:any) {
        addDebugLog('FinanceManagement: Failed to derive auth', { error: e?.message });
        setSchoolID(null);
        setUserRole(null);
      } finally {
        setIsLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Load enabled providers list once we have schoolID
  useEffect(() => {
    const loadProviders = async () => {
      if (!schoolID) { setProviderList([]); return; }
      try {
        const cfgSnap = await getDoc(doc(db, `schools/${schoolID}/paymentConfig/main`));
        const enabled: string[] = [];
        if (cfgSnap.exists()) {
          const data = cfgSnap.data() as any;
          const methods: any[] = data?.paymentMethods || [];
          for (const m of methods) {
            if (m?.enabled && m?.provider) enabled.push(String(m.provider));
          }
        }
        const fallback = ['mpesa','equity','kcb','cash','card'];
        const list = (enabled.length ? enabled : fallback).filter((p,i,a)=>a.indexOf(p)===i);
        setProviderList(list);
        addDebugLog('FinanceManagement: Enabled providers', { providers: list });
      } catch (e:any) {
        addDebugLog('FinanceManagement: Failed to load providers', { error: e?.message });
        setProviderList(['mpesa','equity','kcb','telkom','airtel','standardChartered','familyBank']);
      }
    };
    loadProviders();
  }, [schoolID]);

  // Fetch per-provider balances (API -> Firestore -> mock) for all banking/mobile money providers
  useEffect(() => {
    const allProviders = ['mpesa','equity','kcb','telkom','airtel','standardChartered','familyBank'];
    const enabledProviders = providerList.filter(p => allProviders.includes(p));
    const disabledProviders = allProviders.filter(p => !enabledProviders.includes(p));

    if (!schoolID) return;

    // Initialize all providers: enabled ones start loading, disabled ones get zero balance immediately
    setProviderBalances(prev => {
      const clone = { ...prev };
      for (const p of enabledProviders) {
        if (!clone[p]) clone[p] = { value: null, loading: true, source: 'api' };
        else clone[p] = { ...clone[p], loading: true };
      }
      for (const p of disabledProviders) {
        clone[p] = { value: 0, loading: false, source: 'mock', error: 'Not configured' };
      }
      return clone;
    });

    // Only query enabled providers
    if (enabledProviders.length === 0) return;

    const run = async () => {
      for (const provider of enabledProviders) {
        let value: number | null = null;
        let source: 'api'|'firestore'|'mock' = 'api';
        let error: string | undefined;

        // helper: firestore sum
        const firestoreSum = async (): Promise<number | null> => {
          try {
            const q = query(collection(db, `schools/${schoolID}/transactions/${provider}/records`), where('status','==','completed'));
            const snap = await getDocs(q);
            let total = 0;
            for (const docSnap of snap.docs) {
              const d = docSnap.data() as any;
              const amt = Number(d?.amount || 0) || 0;
              if (!amt) continue;
              const type = String(d?.type || 'credit').toLowerCase();
              total += type === 'debit' ? -amt : amt;
            }
            return total;
          } catch (e:any) {
            addDebugLog('FinanceManagement: Firestore provider sum failed', { provider, error: e?.message });
            return null;
          }
        };

        try {
          if (provider === 'mpesa') {
            try {
              const fn = httpsCallable(functions, 'getMpesaBalance');
              const resp: any = await fn({ schoolId: schoolID });
              if (resp?.data?.success && typeof resp.data.balance === 'number') {
                value = resp.data.balance;
              } else {
                addDebugLog('FinanceManagement: M-Pesa API unexpected response', resp?.data);
              }
            } catch (e:any) {
              error = e?.message;
              addDebugLog('FinanceManagement: M-Pesa API error', { error });
            }
          } else if (provider === 'equity') {
            try {
              const fn = httpsCallable(functions, 'getEquityBalance');
              const resp: any = await fn({ schoolId: schoolID });
              if (resp?.data?.success && typeof resp.data.balance === 'number') {
                value = resp.data.balance;
              } else {
                addDebugLog('FinanceManagement: Equity API unexpected response', resp?.data);
              }
            } catch (e:any) {
              error = e?.message;
              addDebugLog('FinanceManagement: Equity API error', { error });
            }
          } else if (provider === 'kcb') {
            try {
              const fn = httpsCallable(functions, 'getKcbBalance');
              const resp: any = await fn({ schoolId: schoolID });
              if (resp?.data?.success && typeof resp.data.balance === 'number') {
                value = resp.data.balance;
              } else {
                addDebugLog('FinanceManagement: KCB API unexpected response', resp?.data);
              }
            } catch (e:any) {
              error = e?.message;
              addDebugLog('FinanceManagement: KCB API error', { error });
            }
          } else if (provider === 'telkom') {
            try {
              const fn = httpsCallable(functions, 'getTelkomBalance');
              const resp: any = await fn({ schoolId: schoolID });
              if (resp?.data?.success && typeof resp.data.balance === 'number') {
                value = resp.data.balance;
              } else {
                addDebugLog('FinanceManagement: Telkom API unexpected response', resp?.data);
              }
            } catch (e:any) {
              error = e?.message;
              addDebugLog('FinanceManagement: Telkom API error', { error });
            }
          } else if (provider === 'airtel') {
            try {
              const fn = httpsCallable(functions, 'getAirtelBalance');
              const resp: any = await fn({ schoolId: schoolID });
              if (resp?.data?.success && typeof resp.data.balance === 'number') {
                value = resp.data.balance;
              } else {
                addDebugLog('FinanceManagement: Airtel API unexpected response', resp?.data);
              }
            } catch (e:any) {
              error = e?.message;
              addDebugLog('FinanceManagement: Airtel API error', { error });
            }
          } else if (provider === 'standardChartered') {
            try {
              const fn = httpsCallable(functions, 'getStandardCharteredBalance');
              const resp: any = await fn({ schoolId: schoolID });
              if (resp?.data?.success && typeof resp.data.balance === 'number') {
                value = resp.data.balance;
              } else {
                addDebugLog('FinanceManagement: Standard Chartered API unexpected response', resp?.data);
              }
            } catch (e:any) {
              error = e?.message;
              addDebugLog('FinanceManagement: Standard Chartered API error', { error });
            }
          } else if (provider === 'familyBank') {
            try {
              const fn = httpsCallable(functions, 'getFamilyBankBalance');
              const resp: any = await fn({ schoolId: schoolID });
              if (resp?.data?.success && typeof resp.data.balance === 'number') {
                value = resp.data.balance;
              } else {
                addDebugLog('FinanceManagement: Family Bank API unexpected response', resp?.data);
              }
            } catch (e:any) {
              error = e?.message;
              addDebugLog('FinanceManagement: Family Bank API error', { error });
            }
          }
        } catch (e:any) {
          error = e?.message || 'Unknown provider error';
        }

        if (value == null) {
          // try firestore fallback
            const fs = await firestoreSum();
            if (typeof fs === 'number') {
              value = fs;
              source = 'firestore';
            } else {
              value = 0; // mock
              source = 'mock';
            }
        }

        setProviderBalances(prev => ({
          ...prev,
          [provider]: { value, loading: false, source, error }
        }));
        addDebugLog('FinanceManagement: Provider balance computed', { provider, value, source, error });
      }
    };
    run();
  }, [schoolID, providerList]);

  // Calculate outstanding fees for the entire year (all three terms)
  useEffect(() => {
    const computeOutstanding = async () => {
      if (!schoolID) {
        setOutstandingFees(0);
        return;
      }
      setIsLoadingOutstanding(true);
      try {
        // Fetch deployed fee structure
        const feeStructureRef = doc(db, `schools/${schoolID}/deployedFeeStructures/current`);
        const feeStructureSnap = await getDoc(feeStructureRef);
        
        if (!feeStructureSnap.exists()) {
          addDebugLog('FinanceManagement: No deployed fee structure', { schoolID });
          setOutstandingFees(0);
          setIsLoadingOutstanding(false);
          return;
        }
        
        const feeStructure = feeStructureSnap.data();
        const levelFees = feeStructure.levelFees || [];
        
        // Get all students in this school
        const studentsQuery = query(collection(db, `schools/${schoolID}/students`));
        const studentsSnap = await getDocs(studentsQuery);
        
        let totalRequiredAnnual = 0;
        
        // Calculate fees required for each student for entire year (all 3 terms)
        for (const studentDoc of studentsSnap.docs) {
          const studentData = studentDoc.data();
          const studentClass = studentData.className || studentData.class || "";
          const studentGrade = studentData.admissionGrade || studentData.grade || "";
          const studentType = studentData.studentType || "";
          
          // Find matching level fees
          let matchingLevel = null;
          
          if (studentClass) {
            matchingLevel = levelFees.find((level: any) => 
              level.classes && level.classes.some((c: string) => 
                c.toLowerCase() === studentClass.toLowerCase() ||
                studentClass.toLowerCase().includes(c.toLowerCase())
              )
            );
          }
          
          if (!matchingLevel && studentGrade) {
            matchingLevel = levelFees.find((level: any) => 
              level.level && level.level.toLowerCase() === studentGrade.toLowerCase()
            );
          }
          
          if (matchingLevel) {
            const isDaySchool = studentType === "Day School";
            const isOngoing = !!studentData.ongoing;
            
            // Use centralized fee calculation utility
            const applicable = getApplicableTermFees(studentData.admissionDate, matchingLevel, [], isOngoing, isDaySchool);
            
            const studentAnnualFee = applicable.annual;
            totalRequiredAnnual += studentAnnualFee;
          }
        }
        
        // Calculate total payments made across ALL terms and ALL providers
        // Only count payments with accountReference matching Fee-{registrationNumber}
        let totalPaidAnnual = 0;
        const enabledProviders = providerList.length > 0 ? providerList : ['mpesa','equity','kcb','telkom','airtel','standardChartered','familyBank','cash','card'];
        
        for (const provider of enabledProviders) {
          try {
            const q = query(
              collection(db, `schools/${schoolID}/transactions/${provider}/records`),
              where('status', '==', 'completed')
            );
            const snap = await getDocs(q);
            
            for (const docSnap of snap.docs) {
              const d = docSnap.data() as any;
              const amt = Number(d?.amount || 0) || 0;
              if (!amt) continue;
              
              // Only count fee payments (accountReference starts with "Fee-")
              const accRef = String(d?.accountReference || '');
              if (!accRef.toLowerCase().startsWith('fee-')) {
                continue; // Skip donations and other non-fee payments
              }
              
              const type = String(d?.type || 'credit').toLowerCase();
              totalPaidAnnual += type === 'debit' ? -amt : amt;
            }
          } catch (e: any) {
            addDebugLog('FinanceManagement: Failed to fetch annual payments', { provider, error: e?.message });
          }
        }
        
        const outstanding = Math.max(0, totalRequiredAnnual - totalPaidAnnual);
        setOutstandingFees(outstanding);
        addDebugLog('FinanceManagement: Outstanding fees calculated (annual)', { 
          requiredAnnual: totalRequiredAnnual, 
          paidAnnual: totalPaidAnnual, 
          outstanding 
        });
      } catch (e: any) {
        addDebugLog('FinanceManagement: Outstanding fees calculation failed', { error: e?.message });
        setOutstandingFees(0);
      } finally {
        setIsLoadingOutstanding(false);
      }
    };
    computeOutstanding();
  }, [schoolID, providerList]);

  // Summary statistics
  // Compute total revenue as sum of all provider balances
  const providerTotalRevenue = ['mpesa','equity','kcb','telkom','airtel','standardChartered','familyBank']
    .map(p => providerBalances[p]?.value ?? 0)
    .reduce((a, b) => a + b, 0);

  const isLoadingProviderTotal = ['mpesa','equity','kcb','telkom','airtel','standardChartered','familyBank'].some(p => providerBalances[p]?.loading);

  const summaryStats = [
    {
      title: "Total Balance",
      value: providerTotalRevenue,
      description: "All income streams",
      icon: DollarSign,
      color: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-900/20",
      trend: "+12.5%"
    },
    {
      title: "Outstanding Fees for This Term",
      value: "$23,120",
      description: "Pending payments",
      icon: AlertCircle,
      color: "text-red-600",
      bgColor: "bg-red-100 dark:bg-red-900/20",
      trend: "-8.2%"
    },
    {
      title: "Expenses This Year",
      value: "$89,300",
      description: "All three terms",
      icon: Receipt,
      color: "text-blue-600",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
      trend: "+5.1%"
    },
    {
      title: "Net Income",
      value: "$38,150",
      description: "This term",
      icon: TrendingUp,
      color: "text-purple-600",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
      trend: "+18.3%"
    }
  ];

  return (
    <>
      <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 overflow-hidden">
        <SidebarProvider className="h-full overflow-hidden">
          <div className="flex h-full w-full">
            {/* Sidebar section */}
            {userRole === 'admin' || userRole === 'owner' ? (
              <AdminSidebar onSectionChange={() => {}} />
            ) : (
              <TeacherSidebar />
            )}

            {/* Main content area */}
            <div className="flex flex-col flex-1 h-full min-w-0">
              {userRole === 'admin' || userRole === 'owner' ? <Header /> : <Teacherheader />}
              <main className="flex-1 min-h-0 p-3 sm:p-6 overflow-auto">
                {isLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
            {/* Header */}
            <div className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                      Finance Management
                    </h1>
                    <Button size="sm" variant="outline" onClick={() => setShowDebugPanel((v) => !v)} className="h-7 px-2 text-xs">
                      {showDebugPanel ? "Hide Debug" : "Debug"}
                    </Button>
                  </div>
                  <p className="text-sm sm:text-base text-muted-foreground">
                    Complete financial oversight and management system
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs bg-[#2563EB] hover:bg-[#3A72EC] text-white border-none"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    Export Reports
                  </Button>
                  <Button 
                    size="sm" 
                    className="text-xs bg-[#2563EB] hover:bg-[#3A72EC] text-white"
                    onClick={() => setShowNewTransaction(true)}
                  >
                    <PlusCircle className="h-3 w-3 mr-2" />
                    New Transaction
                  </Button>
                  <Button 
                    size="sm" 
                    className="text-xs bg-[#2563EB] hover:bg-[#3A72EC] text-white"
                    onClick={() => setShowFeeStructure(true)}
                  >
                    <FileText className="h-3 w-3 mr-2" />
                    Fee Structure
                  </Button>
                  <Button 
                    size="icon" 
                    variant="outline"
                    className="h-8 w-8 ml-1"
                    onClick={() => setShowSettings(true)}
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

      {/* Fee Structure Modal/Overlay */}
      {showFeeStructure && (
        <SidebarProvider>
          <div className="fixed inset-0 z-50 flex h-screen w-full bg-gray-50 dark:bg-gray-900">
            {userRole === 'admin' || userRole === 'owner' ? (
              <AdminSidebar onSectionChange={() => {}} />
            ) : (
              <TeacherSidebar />
            )}
            <div className="flex flex-col flex-1 h-full">
              {userRole === 'admin' || userRole === 'owner' ? <Header /> : <Teacherheader />}
              <main className="flex-1 p-3 sm:p-6 overflow-auto bg-transparent">
                <CreateFeeStructure 
                  schoolId={schoolID} 
                  onBack={() => setShowFeeStructure(false)} 
                />
              </main>
            </div>
          </div>
        </SidebarProvider>
      )}

      {/* Settings Modal/Overlay */}
      {showSettings && (
        <SidebarProvider>
          <div className="fixed inset-0 z-50 flex h-screen w-full bg-gray-50 dark:bg-gray-900">
            {userRole === 'admin' || userRole === 'owner' ? (
              <AdminSidebar onSectionChange={() => {}} />
            ) : (
              <TeacherSidebar />
            )}
            <div className="flex flex-col flex-1 h-full min-w-0">
              {userRole === 'admin' || userRole === 'owner' ? <Header /> : <Teacherheader />}
              <main className="flex-1 min-h-0 overflow-auto bg-transparent">
                <FinanceSettings 
                  schoolId={schoolID} 
                  onBack={() => setShowSettings(false)} 
                />
              </main>
            </div>
          </div>
        </SidebarProvider>
      )}

      {/* New Transaction Modal/Overlay */}
      {showNewTransaction && (
        <SidebarProvider>
          <div className="fixed inset-0 z-50 flex h-screen w-full bg-gray-50 dark:bg-gray-900">
            {userRole === 'admin' || userRole === 'owner' ? (
              <AdminSidebar onSectionChange={() => {}} />
            ) : (
              <TeacherSidebar />
            )}
            <div className="flex flex-col flex-1 h-full min-w-0">
              {userRole === 'admin' || userRole === 'owner' ? <Header /> : <Teacherheader />}
              <main className="flex-1 min-h-0 overflow-auto bg-transparent">
                <NewTransaction 
                  schoolId={schoolID} 
                  onBack={() => setShowNewTransaction(false)} 
                />
              </main>
            </div>
          </div>
        </SidebarProvider>
      )}

      {/* Debug Panel */}
      {showDebugPanel && (
        <div className="fixed bottom-0 right-0 z-50 w-full max-w-xl bg-black text-white p-4 rounded-t-lg shadow-lg overflow-y-auto max-h-96 border-t-4 border-blue-500">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold">Finance Debug</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigator.clipboard?.writeText(debugLogs.join("\n\n"))}>Copy</Button>
              <Button size="sm" variant="outline" onClick={() => setShowDebugPanel(false)}>Close</Button>
            </div>
          </div>
          <pre className="text-xs whitespace-pre-wrap max-h-80 overflow-y-auto">{debugLogs.join("\n\n") || "No debug logs yet. Errors and rejections will appear here."}</pre>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {summaryStats.map((stat, index) => (
          <Card key={index} className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-4">
              <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-3 w-3 sm:h-4 sm:w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              <div className="text-lg sm:text-2xl font-bold text-foreground">
                {stat.title === 'Total Balance'
                  ? (isLoadingProviderTotal ? <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></span> : fmtKES(providerTotalRevenue))
                  : stat.title === 'Outstanding Fees for This Term'
                    ? (isLoadingOutstanding ? <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></span> : fmtKES(outstandingFees))
                    : stat.value}
              </div>
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-muted-foreground">
                  {stat.description}
                </p>
                <span className={`text-xs font-medium ${
                  stat.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.trend}
                </span>
              </div>
              {/* Net Income card left untouched intentionally */}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Provider Balances Section */}
      {Object.keys(providerBalances).length > 0 && (
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {['mpesa','equity','kcb','telkom','airtel','standardChartered','familyBank'].map(provider => {
            const data = providerBalances[provider];
            const label = provider === 'mpesa' ? 'M-Pesa' 
              : provider === 'telkom' ? 'Telkom (T-Kash)'
              : provider === 'airtel' ? 'Airtel Money'
              : provider === 'standardChartered' ? 'Standard Chartered'
              : provider === 'familyBank' ? 'Family Bank'
              : provider.toUpperCase();
            return (
              <Card key={provider} className="hover:shadow-md transition-all duration-300">
                <CardHeader className="p-3 sm:p-4 pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center">
                    {provider === 'mpesa' && (
                      <img src={mpesaLogo} alt="M-Pesa" className="h-7 w-7 sm:h-9 sm:w-9 object-contain rounded-md mr-1" style={{padding: 0}} />
                    )}
                    {provider === 'equity' && (
                      <img src={equityLogo} alt="Equity" className="h-7 w-7 sm:h-9 sm:w-9 object-contain rounded-md mr-1" style={{padding: 0}} />
                    )}
                    {provider === 'kcb' && (
                      <img src={kcbLogo} alt="KCB" className="h-7 w-7 sm:h-9 sm:w-9 object-contain rounded-md mr-1" style={{padding: 0}} />
                    )}
                    {provider === 'telkom' && (
                      <img src={telkomLogo} alt="Telkom" className="h-7 w-7 sm:h-9 sm:w-9 object-contain rounded-md mr-1" style={{padding: 0}} />
                    )}
                    {provider === 'airtel' && (
                      <img src={airtelLogo} alt="Airtel" className="h-7 w-7 sm:h-9 sm:w-9 object-contain rounded-md mr-1" style={{padding: 0}} />
                    )}
                    {provider === 'standardChartered' && (
                      <img src={standardCharteredLogo} alt="Standard Chartered" className="h-7 w-7 sm:h-9 sm:w-9 object-contain rounded-md mr-1" style={{padding: 0}} />
                    )}
                    {provider === 'familyBank' && (
                      <img src={familyBankLogo} alt="Family Bank" className="h-7 w-7 sm:h-9 sm:w-9 object-contain rounded-md mr-1 shadow-lg dark:shadow-none" style={{padding: 0}} />
                    )}
                    {label} Balance
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                    <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="text-lg sm:text-xl font-bold text-foreground">
                    {data?.loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : fmtKES(data?.value)}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Source: {data?.source === 'api' ? 'API' : data?.source === 'firestore' ? 'Transactions' : 'Mock (integration issue)'}
                  </p>
                  {data?.error && data?.source === 'mock' && (
                    <p className="text-[11px] mt-1 text-yellow-600 dark:text-yellow-400">Fallback used: {data.error}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto">
          <TabsList className="grid w-full grid-cols-6 h-auto p-1">
            <TabsTrigger value="dashboard" className="text-xs px-2 py-2">
              <TrendingUp className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="fees" className="text-xs px-2 py-2">
              <Users className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Student Fees</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="text-xs px-2 py-2">
              <CreditCard className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Payments</span>
            </TabsTrigger>
            <TabsTrigger value="expenses" className="text-xs px-2 py-2">
              <Receipt className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Expenses</span>
            </TabsTrigger>
            <TabsTrigger value="payroll" className="text-xs px-2 py-2">
              <FileText className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Payroll</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="text-xs px-2 py-2">
              <FileText className="h-3 w-3 mr-1" />
              <span className="hidden sm:inline">Reports</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="dashboard" className="space-y-4">
          <FinanceDashboard />
        </TabsContent>

        <TabsContent value="fees" className="space-y-4">
          <StudentFeeManagement />
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <PaymentProcessing />
        </TabsContent>

        <TabsContent value="expenses" className="space-y-4">
          <ExpenseManagement />
        </TabsContent>

        <TabsContent value="payroll" className="space-y-4">
          <PayrollManagement />
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <FinanceReports />
        </TabsContent>
      </Tabs>
      </>
      )}
              </main>
            </div>
          </div>
        </SidebarProvider>
      </div>
    </>
  );
};

export default FinanceManagement;